import React, { useState, useEffect, useRef } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, TextField, Switch, FormControlLabel, Chip, OutlinedInput, Grid, Collapse } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { ITargetMarginSearchParams } from 'app/models/GlobalSearch/GlobalSearch';
import { CustomFilter } from 'app/ComponentLibrary/Filter';
import { MarginFilterSpecs, MarginFilterNames } from '../Margin/MarginFilterUtils';
import { getSearchFilterOptions } from 'app/services/SharedServices/SharedServices';
import { getMultiSelectOptions } from '../helper';
import { candidateSearchFilterName } from '../Candidate/SearchUtils';
import { useDispatch, useSelector } from 'react-redux';
import { gridStateActions } from 'app/components/Common/Grid/GridStateManagement/GridState.redux';
import { selectGridState, selectGridFiltersExpanded } from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { useFormContext } from 'react-hook-form';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { initialFacilityAggregateSearch } from 'app/ComponentLibrary/Filter/FacilitySearch/Index';

const useStyles = makeStyles()((theme) => ({
  filterCollapse: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '3px',
    minWidth: '82px',
    flexShrink: 0,
  },
  collapse: {
    paddingTop: '4px',
  },
}));

interface TargetMarginSearchFiltersProps {
  defaultFilterValues?: ITargetMarginSearchParams;
  onApplyFilter: (filters: ITargetMarginSearchParams) => void;
}

export const TargetMarginSearchFilters: React.FC<TargetMarginSearchFiltersProps> = ({
  defaultFilterValues = {},
  onApplyFilter,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const gridState = useSelector(selectGridState);
  const { setValue, reset, getValues } = useFormContext();
  
  const [filters, setFilters] = useState<ITargetMarginSearchParams>({
    pageNumber: 1,
    pageSize: 100,
    marginType: 'Order',
    isActive: true,
    sortColumn: 'RowKey',
    sortDirection: 'ASC',
    ...defaultFilterValues,
  });

  const [divisionIds, setDivisionIds] = useState<string[]>([]);
  const [facilityIds, setFacilityIds] = useState<string[]>([]);
  const [orderIds, setOrderIds] = useState<string[]>([]);
  const [disciplineIds, setDisciplineIds] = useState<string[]>([]);
  const [specialtyIds, setSpecialtyIds] = useState<string[]>([]);
  const [subSpecialtyIds, setSubSpecialtyIds] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [targetMarginSourceIds, setTargetMarginSourceIds] = useState<string[]>([]);
  const [skillSetFilter, setSkillSetFilter] = useState<any>(null);
  const [positions, setPositions] = useState<any>(null);
  const [targetMarginDefault, setTargetMarginDefault] = useState<any>(null);
  const [defaultWeeklyPay, setDefaultWeeklyPay] = useState<any>(null);
  const [payRangeMinMaxPercentage, setPayRangeMinMaxPercentage] = useState<any>(null);

  // Initialize filter specs
  const filterSpecs = React.useRef({
    divisionOpts: [],
    stateOpts: [],
  });

  const gridSpecs = React.useRef(new MarginFilterSpecs(filterSpecs.current, {}));
  const [fields, setFields] = useState(gridSpecs.current.getMarginFilterSpecs());
  const filtersExpanded = useSelector(selectGridFiltersExpanded);
  const handleChange = () => {
    dispatch(gridStateActions.setFiltersExpanded(!filtersExpanded));
  };

  useEffect(() => {
    let isMounted = true;
    
    getSearchFilterOptions('divisions', true).then(res => {
      if (isMounted) {
        filterSpecs.current.divisionOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
        const fieldsSchema = gridSpecs.current.getMarginFilterSpecs();
        setFields(fieldsSchema);
      }
    });
    getSearchFilterOptions('states', true).then(res => {
      if (isMounted) {
        filterSpecs.current.stateOpts = getMultiSelectOptions(res || [], { withoutAllOption: true });
        const fieldsSchema = gridSpecs.current.getMarginFilterSpecs();
        setFields(fieldsSchema);
      }
    });
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Trigger initial search only once when component mounts
  useEffect(() => {
    // Use setTimeout to ensure form is fully initialized
    const timer = setTimeout(() => {
      // Set default values for Positions and Margin Type
      setValue('marginType', 'Order');
      setValue('positions', { id: 1, value: { min: 1, max: 999 }, name: '>0' });
      
      // Apply the default filters
      const defaultFilters = {
        ...filters,
        marginType: 'Order',
        positions: { id: 1, value: { min: 1, max: 999 }, name: '>0' }
      };
      onApplyFilter(defaultFilters);
    }, 0);

    return () => clearTimeout(timer);
  }, []); // Empty dependency array ensures this only runs once on mount

  const handleFilterChange = (key: keyof ITargetMarginSearchParams, value: any) => {
    const newFilters = {
      ...filters,
      [key]: value,
    };
    setFilters(newFilters);
  };

  const handleArrayFilterChange = (key: string, value: string[]) => {
    const jsonString = value.length > 0 ? JSON.stringify(value) : undefined;
    handleFilterChange(key as keyof ITargetMarginSearchParams, jsonString);
  };

  const handleAddChip = (value: string, setter: (value: string[]) => void, key: string) => {
    if (value.trim()) {
      const currentArray = key === 'divisionIds' ? divisionIds : 
                          key === 'facilityIds' ? facilityIds :
                          key === 'orderIds' ? orderIds :
                          key === 'disciplineIds' ? disciplineIds :
                          key === 'specialtyIds' ? specialtyIds :
                          key === 'subSpecialtyIds' ? subSpecialtyIds :
                          key === 'states' ? states :
                          key === 'targetMarginSourceIds' ? targetMarginSourceIds : [];
      
      if (!currentArray.includes(value.trim())) {
        const newValues = [...currentArray, value.trim()];
        setter(newValues);
        handleArrayFilterChange(key, newValues);
      }
    }
  };

  const handleRemoveChip = (index: number, setter: (value: string[]) => void, key: string) => {
    const currentArray = key === 'divisionIds' ? divisionIds : 
                        key === 'facilityIds' ? facilityIds :
                        key === 'orderIds' ? orderIds :
                        key === 'disciplineIds' ? disciplineIds :
                        key === 'specialtyIds' ? specialtyIds :
                        key === 'subSpecialtyIds' ? subSpecialtyIds :
                        key === 'states' ? states :
                        key === 'targetMarginSourceIds' ? targetMarginSourceIds : [];
    
    const newValues = currentArray.filter((_, i) => i !== index);
    setter(newValues);
    handleArrayFilterChange(key, newValues);
  };

  const resetFilter = () => {
    setDivisionIds([]);
    setFacilityIds([]);
    setTargetMarginSourceIds([]);
    setOrderIds([]);
    setDisciplineIds([]);
    setSkillSetFilter(null);
    setPositions({ id: 1, value: { min: 1, max: 999 }, name: '>0' });
    setStates([]);
    setTargetMarginDefault(null);
    setDefaultWeeklyPay(null);
    setPayRangeMinMaxPercentage(null);
    
    const defaultFilters = {
      pageNumber: 1,
      pageSize: 100,
      marginType: 'Order',
      isActive: true,
      sortColumn: 'RowKey',
      sortDirection: 'ASC',
    };
    setFilters(defaultFilters);
    
    // Reset form with default values
    reset({
      marginType: 'Order',
      division: [],
      facility: initialFacilityAggregateSearch,
      targetMarginSource: [],
      orderId: [],
      disciplineIds: [],
      skillsetFilter: null,
      positions: { id: 1, value: { min: 1, max: 999 }, name: '>0' },
      states: [],
      defaultTargetMargin: null,
      defaultWeeklyPay: null,
      payRangeMinMaxPercentage: null,
    });
    
    onApplyFilter({
      ...defaultFilters,
      divisionIds: undefined,
      facilityIds: undefined,
      targetMarginSourceIds: undefined,
      orderIds: undefined,
      disciplineIds: undefined,
      skillsetFilter: undefined,
      positions: { id: 1, value: { min: 1, max: 999 }, name: '>0' },
      states: undefined,
      defaultTargetMargin: undefined,
      defaultWeeklyPay: undefined,
      payRangeMinMaxPercentage: undefined,
    } as ITargetMarginSearchParams);
  };

  return (
    <Box sx={{ marginLeft: '-6px', marginRight: '-6px', paddingLeft: 0, paddingRight: 0, paddingTop: '12px' }}>
      <Grid container spacing={2} sx={{ flexWrap: 'nowrap' }}>
        <Grid item flex="1 1 auto" sx={{ minWidth: 0 }}>
        <Collapse timeout={200} in={filtersExpanded} collapsedSize={45} className={classes.collapse}>
          <CustomFilter
            fields={fields
              .map(spec => spec.name === MarginFilterNames.facility ? { ...spec, applyOnClickAway: true, applyOnEnter: true } : spec)
              .filter(spec => 
                spec.name === MarginFilterNames.marginType || 
                spec.name === MarginFilterNames.targetMarginSource ||
                spec.name === MarginFilterNames.division ||
                spec.name === MarginFilterNames.facility ||
                spec.name === MarginFilterNames.orderId ||
                spec.name === candidateSearchFilterName.candidateSkillSet ||
                spec.name === MarginFilterNames.positions ||
                spec.name === MarginFilterNames.states ||
                spec.name === MarginFilterNames.targetMarginDefault ||
                spec.name === MarginFilterNames.defaultWeeklyPay ||
                spec.name === MarginFilterNames.payRangeMinMaxPercentage
              )
              .map(spec => ({ ...spec, applyOnEnter: false }))
            }
            justifyContent={'flex-start'}
            version2
            resetBtn
            onChangeValues={(name, value) => {
              if (name === MarginFilterNames.marginType) {
                setValue('marginType', value);
                if (value === 'Order') {
                  setValue('positions', { id: 1, value: { min: 1, max: 999 }, name: '>0' });
                } else {
                  setValue('positions', null);
                }
              } else if (name === MarginFilterNames.targetMarginDefault) {
                setTargetMarginDefault(value);
                setValue('defaultTargetMargin', value);
              }
            }}
            onApply={(name, value) => {
              if (name === MarginFilterNames.division) {
                const selections = Array.isArray(value) ? value : [value];
                setDivisionIds(selections);
                setValue('division', selections);
              } else if (name === MarginFilterNames.orderId) {
                const selections = Array.isArray(value) ? value : [value];
                setOrderIds(selections);
                setValue('orderId', selections);
              } else if (name === MarginFilterNames.targetMarginSource) {
                const selections = Array.isArray(value) ? value : [value];
                setTargetMarginSourceIds(selections);
                setValue('targetMarginSource', selections);
              } else if (name === MarginFilterNames.facility) {
                const aggregate = value || {};
                const selections = Array.isArray(aggregate?.selections) ? aggregate.selections : [];
                setFacilityIds(selections);
                // Persist the entire aggregate so the control retains its state
                setValue('facility', aggregate);
              } else if (name === MarginFilterNames.marginType) {
                setValue('marginType', value);
              } else if (name === candidateSearchFilterName.candidateSkillSet) {
                setSkillSetFilter(value);
                setValue('skillsetFilter', value);
              } else if (name === MarginFilterNames.positions) {
                setPositions(value);
                setValue('positions', value);
              } else if (name === MarginFilterNames.states) {
                setStates(Array.isArray(value) ? value : [value]);
                setValue('states', value);
              } else if (name === MarginFilterNames.targetMarginDefault) {
                setTargetMarginDefault(value);
                setValue('defaultTargetMargin', value);
              } else if (name === MarginFilterNames.defaultWeeklyPay) {
                setDefaultWeeklyPay(value);
                setValue('defaultWeeklyPay', value);
              } else if (name === MarginFilterNames.payRangeMinMaxPercentage) {
                setPayRangeMinMaxPercentage(value);
                setValue('payRangeMinMaxPercentage', value);
              }
               const currentValues = getValues();
              onApplyFilter(currentValues);
            }}
            analytics={{
              caller: t('search.globalSearch.globalSearchFilter'),
              onApply: true,
              properties: {
                category: 'targetmargins',
              },
            }}
          />
        </Collapse>
      </Grid>
      <Grid item flex="0 0 auto" className={classes.filterCollapse}>
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', minWidth: '82px' }}>
          <SearchPageIcons
            resetlbutton
            funnelButton
            checked={filtersExpanded}
            onResetForm={() => resetFilter()}
            onAdvancedFilterClick={() => handleChange()}
          />
        </Box>
      </Grid>      
      </Grid>
    </Box>
  );
};
